export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQCategory {
  title: string;
  items: FAQItem[];
}

export const FAQ_CATEGORIES: FAQCategory[] = [
  {
    title: 'Getting Started',
    items: [
      {
        question: 'What is the Session Code?',
        answer: 'The Session Code is a unique identifier for your game session. A code is auto-generated on the home screen along with a QR code, or you can type in a code shared by your GM. Players can scan the QR code to join instantly, or enter the code manually. Any platform (Foundry VTT, Discord) can also connect to the same session using this code. If no session exists for the code yet, one is created automatically when you connect.'
      },
      {
        question: 'What is the Player Name?',
        answer: 'Your Player Name is your display name in the game session. This is how other participants will identify you when you submit dice rolls. It is also the name used when linking your identity via the Discord /frog-link-me command. Choose a name that matches your character or your real name.'
      },
      {
        question: 'Do I need an account?',
        answer: 'No — guests can use the app without signing up. Creating a free account unlocks extra features: your player name is auto-set from your username, sessions and settings sync across devices, you can register custom dice for personalised recognition, and you get access to Accurate recognition mode (GPT-5.2). You can sign up any time from Settings.'
      },
      {
        question: 'How do I install Dice Frog on my phone?',
        answer: 'Dice Frog works best when installed to your home screen as an app. On Android: open the site in Chrome, tap the three-dot menu, and select "Add to Home Screen" or "Install app". On iOS: open the site in Safari, tap the Share button, and select "Add to Home Screen". Installing enables push notifications so your phone can wake up when a roll request arrives, even if the screen is off.'
      },
      {
        question: 'What are Saved Sessions?',
        answer: 'Saved Sessions store your session code and player name so you can quickly reconnect to the same game session without re-entering everything. Create a session for each campaign you play in regularly. When you return, simply tap the Connect button on your session card to reconnect.'
      }
    ]
  },
  {
    title: 'Dice Recognition',
    items: [
      {
        question: 'What dice are supported?',
        answer: 'd4, d6, d8, d10, d12, d20, and d100 (percentile). Standard polyhedral dice work best.'
      },
      {
        question: 'Why is recognition sometimes inaccurate?',
        answer: 'Recognition accuracy depends on lighting, dice visibility, and dice style. Ensure good lighting, place dice flat with numbers facing up, and avoid highly stylized or decorative dice.'
      },
      {
        question: 'What are the Fast and Accurate recognition modes?',
        answer: 'Fast mode uses GPT-4.1 mini for quick recognition at lower cost (~$0.0004/image). Accurate mode uses GPT-5.2 for the best accuracy with challenging dice, unusual lighting, or stylized fonts (~$0.01/image). Most users should start with Fast mode and switch to Accurate only if experiencing recognition issues.'
      }
    ]
  },
  {
    title: 'Platforms & Integrations',
    items: [
      {
        question: 'Which platforms are supported?',
        answer: 'Dice Frog works with Foundry VTT and Discord, with Roll20, Owlbear Rodeo, and D&D Beyond planned. The web app is platform-agnostic - enter a session code and your name to get started. Platforms connect independently to the same session using the room code.'
      },
      {
        question: 'How does Discord integration work?',
        answer: 'A GM or server admin uses /frog-link in a Discord channel to link it to a session code. The session must already exist (connect from the web app or a VTT first). Once linked, players can use /frog formula:XdY in Discord to request a physical dice roll — the result is posted back to the channel as a rich embed including a photo of the dice. Players can run /frog-link-me to associate their Discord identity with their player name for targeted roll requests.'
      },
      {
        question: 'What is the Server URL setting?',
        answer: 'The Server URL is the WebSocket address that relays dice rolls between your phone and the game session. This is pre-configured for the official Dice Frog service. Most users should never need to change this.'
      }
    ]
  },
  {
    title: 'Dice Registration',
    items: [
      {
        question: 'What is Dice Registration?',
        answer: 'Dice Registration lets you train the system to recognize your specific dice set. By photographing each face of your dice, you create a custom recognition model that improves accuracy for your unique dice designs, colors, and styles.'
      },
      {
        question: 'How do I register my dice?',
        answer: 'Go to Settings and tap "Register Dice". Select a dice type, then photograph each face. Tap the capture button to take photos, or press and hold it to enable Auto-Capture mode where the app captures automatically when the die is stable. You can also use Video Capture to record rolling clips. Swipe up on the drawer handle to access face selection and view your captured images. Training takes 2-4 hours.'
      },
      {
        question: 'Why would I register my dice?',
        answer: 'Custom dice with unique fonts, colors, or designs may not be recognized well by the default model. Registering your dice trains a personalized model that dramatically improves recognition accuracy for your specific dice set.'
      },
      {
        question: 'How many images are needed?',
        answer: 'The number depends on dice type - d6 needs 30-40 images while d20 needs 100-140. Video capture makes this easy by extracting multiple frames per roll. The app shows your coverage progress and prompts you to capture any missing faces.'
      },
      {
        question: 'How long does dice training take?',
        answer: 'Training typically takes 2-4 hours. You can continue using the app normally during this time. Check the training status on the Register page - your model will automatically become available once training completes.'
      }
    ]
  },
  {
    title: 'Capture Modes',
    items: [
      {
        question: 'What is Video Capture mode?',
        answer: 'Video Capture lets you record short clips of rolling your dice in a tray. The app automatically extracts frames when the dice settle, detects which face is showing, and tracks which faces still need more images. This is faster and produces better training data than manual photography.'
      },
      {
        question: 'What is Auto-Capture?',
        answer: 'Auto-Capture automatically takes photos when your dice stops moving. To enable it, press and hold the capture button until it switches to Play/Pause controls. Position the die, remove your hand, and wait about 1.5 seconds for automatic capture. Use the Pause button to temporarily stop captures, or press and hold either Play or Pause to return to manual capture mode.'
      },
      {
        question: 'Why does the app reject some photos?',
        answer: 'Quality gates ensure your training data is clear and consistent. Photos may be rejected for being too blurry (hold camera steady), too dark or bright (adjust lighting), or having low contrast (use a contrasting background like a black dice tray). Follow the on-screen guidance to capture acceptable images.'
      }
    ]
  },
  {
    title: 'Local Bridge & Storage',
    items: [
      {
        question: 'What is Local Bridge mode?',
        answer: 'Local Bridge lets you upload dice registration images directly to your PC over WiFi, bypassing cloud storage entirely. Run the bridge_server.py script on your PC, then configure the Bridge URL in Settings. Your phone sends images directly to your computer for local training.'
      },
      {
        question: 'How do I set up Local Bridge?',
        answer: 'On your PC, run "npm run trainer:bridge" from the Dice Frog folder (or "python bridge_server.py" in packages/dice-trainer). The server displays a URL like http://192.168.1.100:8080. In the app, go to Settings > Storage Configuration, select Local Bridge, enter that URL, test the connection, and save.'
      },
      {
        question: 'Why use Local Bridge instead of Cloud?',
        answer: 'Local Bridge keeps all your training data on your own PC - useful for privacy, faster local training, or offline workflows. Images go directly from phone to PC without passing through AWS servers.'
      },
      {
        question: 'What is Download to Phone mode?',
        answer: 'Download to Phone saves dice images directly to your device (usually the Downloads folder). After capturing, you can transfer the files to your PC via USB cable, email, or cloud drive. This works offline without any network configuration.'
      },
      {
        question: 'How do I transfer downloaded images to my PC?',
        answer: 'Connect your phone to your PC with a USB cable and copy the images from Downloads. Alternatively, upload them to Google Drive, Dropbox, or email them to yourself. Place the images in the dice-trainer/data folder organized by dice type (e.g., data/d20/1/, data/d20/2/, etc.).'
      }
    ]
  }
];

export const PRIVACY_ITEMS: FAQItem[] = [
  {
    question: 'What data does Dice Frog collect?',
    answer: 'Dice Frog collects only what\'s needed to run your game session: a session code, your chosen player name, and temporary WebSocket connection data. No email addresses, passwords, or real-world identity information is collected. If you create an account, your username and authentication details are stored securely.'
  },
  {
    question: 'How is my data stored and for how long?',
    answer: 'Session data is stored in AWS DynamoDB hosted in the EU (Ireland). Unnamed sessions are automatically deleted after 24 hours. Named sessions persist for up to 365 days. All data deletion is handled automatically — there is nothing you need to do.'
  },
  {
    question: 'Is my camera feed stored or transmitted?',
    answer: 'Camera frames are processed entirely on your device for dice recognition and are never stored. When submitting a roll result, the captured dice image may be temporarily stored in cloud storage (up to 24 hours) so platforms like Discord can display it inline. No images are retained beyond this window.'
  },
  {
    question: 'Does Dice Frog use cookies or tracking?',
    answer: 'No cookies are used. The app uses your browser\'s local storage to save session preferences (like your player name and session code) so you don\'t have to re-enter them. No analytics, advertising trackers, or third-party tracking scripts are present.'
  },
  {
    question: 'What third-party services are used?',
    answer: 'Dice recognition may use OpenAI\'s vision API (GPT-4.1 mini or GPT-5.2) to identify dice values from captured images. Images sent to OpenAI are processed and discarded according to their data usage policy. AWS services (DynamoDB, Lambda, API Gateway, S3) host the backend infrastructure in the EU (Ireland) region.'
  },
  {
    question: 'Can I delete my data?',
    answer: 'Yes. Unnamed sessions delete automatically after 24 hours. You can clear your locally saved sessions from the app at any time by removing them from your session list. For account deletion or any data concerns, contact the developer directly.'
  }
];
